import React, { useState, useEffect } from 'react';
import { X, Save, Camera, Check, Plus, Package, Scale, Ruler, Search } from 'lucide-react';
import { useStore } from '../store';
import { translations } from '../types';
import { Scanner } from './Scanner';
import { supabase, fetchAvailableTrackingNumbers } from '../lib/api';
import type { OrderTracking } from '../types';
import { Loader2 } from 'lucide-react';

interface Box {
  id: number;
  weight: number;
  width: number;
  height: number;
  depth: number;
}

interface CreateShipmentProps {
  onClose: () => void;
}

export function CreateShipment({ onClose }: CreateShipmentProps) {
  const { language } = useStore();
  const t = translations[language];
  const [trackingNumbers, setTrackingNumbers] = useState<Array<{
    id: number;
    number: string;
    status_date: Date;
    comment: string;
    checked: boolean;
    order_id?: string;
  }>>([]);
  const [comments, setComments] = useState<Record<number, string>>({});
  const [editingId, setEditingId] = useState<number | null>(null);
  const [childTrackingNumbers, setChildTrackingNumbers] = useState<Record<number, string[]>>({});
  const [addingChildTo, setAddingChildTo] = useState<number | null>(null);
  const [newChildTracking, setNewChildTracking] = useState('');
  const [showScanner, setShowScanner] = useState(false);
  const [shipmentNumber, setShipmentNumber] = useState('ЯР-');
  const [rawInput, setRawInput] = useState('');
  const [boxes, setBoxes] = useState<Box[]>([{ id: 1, weight: 0, width: 0, height: 0, depth: 0 }]);
  const [searchQuery, setSearchQuery] = useState('');
  const [lastChecked, setLastChecked] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<any>(null);

  // Subscribe to real-time changes
  useEffect(() => {
    setIsLoading(true);
    setError(null);

    loadAvailableTrackingNumbers();
    
    // Subscribe to orders table changes
    const channel = supabase
      .channel('orders_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'orders'
        },
        (payload) => {
          // Reload tracking numbers when tracking_statuses changes
          if (payload.new && 
              JSON.stringify(payload.new.tracking_statuses) !== 
              JSON.stringify(payload.old?.tracking_statuses)) {
            loadAvailableTrackingNumbers();
          }
        }
      )
      .subscribe();

    setSubscription(channel);

    return () => {
      if (subscription) {
        supabase.removeChannel(subscription);
      }
    };
  }, []);

  const loadAvailableTrackingNumbers = async () => {
    try {
      const { success, data, error } = await fetchAvailableTrackingNumbers();
      
      if (success && data) {
        // Sort by most recent first
        const formattedData = data.map((item, index) => ({
          id: index + 1,
          number: item.tracking_number,
          status_date: new Date(item.status_date),
          comment: '',
          checked: false,
          order_id: item.order_id,
        }));
        
        setTrackingNumbers(formattedData);
        setError(null);
      } else {
        console.error('Error loading available tracking numbers:', error);
        setError(t.loadError);
      }
    } catch (err) {
      console.error('Error in loadAvailableTrackingNumbers:', err);
      setError(t.loadError);
    } finally {
      setIsLoading(false);
    }
  };

  const addBox = () => {
    setBoxes([...boxes, { 
      id: boxes.length + 1, 
      weight: 0, 
      width: 0, 
      height: 0, 
      depth: 0 
    }]);
  };

  const updateBox = (id: number, field: keyof Box, value: number) => {
    setBoxes(boxes.map(box => 
      box.id === id ? { ...box, [field]: value } : box
    ));
  };

  const removeBox = (id: number) => {
    if (boxes.length > 1) {
      setBoxes(boxes.filter(box => box.id !== id));
    }
  };

  const calculateVolume = (box: Box) => {
    // Convert from cm to m by dividing by 100
    const width = box.width / 100;
    const height = box.height / 100;
    const depth = box.depth / 100;
    return width * height * depth;
  };

  const getTotalWeight = () => {
    return boxes.reduce((sum, box) => sum + box.weight, 0);
  };

  const getTotalVolume = () => {
    return boxes.reduce((sum, box) => sum + calculateVolume(box), 0);
  };

  const handleShipmentNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    let numbers = input.replace(/[^0-9]/g, '');
    
    // Limit to 7 digits (4 before dash, 3 after)
    if (numbers.length > 7) {
      numbers = numbers.slice(0, 7);
    }
    
    setRawInput(numbers);
    
    // Format with ЯР prefix and hyphens
    let formatted = 'ЯР';
    if (numbers.length > 0) {
      formatted += '-' + numbers.slice(0, 4);
      if (numbers.length > 4) {
        formatted += '-' + numbers.slice(4);
      }
    }
    setShipmentNumber(formatted);
  };

  const handleCommentChange = (id: number, comment: string) => {
    setComments(prev => ({ ...prev, [id]: comment }));
  };

  const toggleChecked = (id: number) => {
    setTrackingNumbers(prev => 
      prev.map(track => 
        track.id === id ? { ...track, checked: !track.checked } : track
      )
    );
  };

  const handleCommentSave = (id: number) => {
    if (newChildTracking) {
      setChildTrackingNumbers(prev => ({
        ...prev,
        [id]: [...(prev[id] || []), newChildTracking]
      }));
      setNewChildTracking('');
    }
    setAddingChildTo(null);
  };

  const removeChildTracking = (parentId: number, childTracking: string) => {
    setChildTrackingNumbers(prev => ({
      ...prev,
      [parentId]: prev[parentId].filter(t => t !== childTracking)
    }));
  };

  const handleScanResult = (scannedNumber: string) => {
    const found = trackingNumbers.find(
      track => track.number.toLowerCase() === scannedNumber.toLowerCase()
    );
    
    if (found) {
      toggleChecked(found.id);
      setShowScanner(false);
    }
  };

  const handleSendShipment = async () => {
    try {
      setIsSaving(true);
      setError(null);
      
      // Validate shipment number format
      if (!shipmentNumber.match(/^ЯР-\d{4}-\d{1,3}$/)) {
        throw new Error(t.invalidShipmentNumber);
      }
      
      const selectedTrackingNumbers = trackingNumbers
        .filter(t => t.checked)
        .map(t => ({
          tracking_number: t.number,
          child_trackings: childTrackingNumbers[t.id] || []
        }));

      if (selectedTrackingNumbers.length === 0) {
        throw new Error('Выберите хотя бы один трек-номер');
      }

      if (boxes.some(box => !box.weight || !box.width || !box.height || !box.depth)) {
        throw new Error('Заполните все размеры и вес коробок');
      }

      // Calculate totals
      const totalWeight = getTotalWeight();
      const totalVolume = getTotalVolume();
      const density = totalVolume > 0 ? totalWeight / totalVolume : 0;

      const shipmentData = {
        shipment_number: shipmentNumber,
        tracking_numbers: selectedTrackingNumbers.map(t => ({
          ...t,
          status: 'completed'
        })),
        boxes: JSON.stringify(boxes.map(box => ({
          weight: box.weight,
          width: box.width,
          height: box.height,
          depth: box.depth
        }))),
        total_weight: totalWeight,
        total_volume: totalVolume,
        density: density,
        status: 'completed'
      };

      console.log('Saving shipment:', shipmentData);
      
      const { error: saveError } = await supabase
        .from('shipments')
        .insert(shipmentData);

      if (saveError) {
        console.error('Error saving shipment:', saveError);
        throw new Error(
          saveError.message === 'duplicate key value violates unique constraint "shipment_number_unique"'
            ? 'Номер отправления уже существует'
            : saveError.message || 'Ошибка при сохранении'
        );
      }

      // Remove used tracking numbers from the list
      setTrackingNumbers(prevNumbers => 
        prevNumbers.filter(t => !selectedTrackingNumbers.some(s => s.number === t.number))
      );

      // Clear selections and comments for remaining tracking numbers
      setTrackingNumbers(prev => prev.map(t => ({ ...t, checked: false })));
      setComments({});
      
      // Reset boxes to initial state
      setBoxes([{ id: 1, weight: 0, width: 0, height: 0, depth: 0 }]);
      
      // Reset shipment number
      setShipmentNumber('ЯР-');
      setRawInput('');

      // Show success notification
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 left-1/2 transform -translate-x-1/2 bg-green-500 text-white px-6 py-4 rounded-lg shadow-lg z-50 flex flex-col items-center text-center animate-fade-in';
      notification.innerHTML = `
        <div class="text-lg font-semibold mb-1">${t.shipmentCreated}</div>
        <div class="text-sm opacity-90">${t.thankYou}</div>
      `;
      document.body.appendChild(notification);

      // Add success animation to the notification
      setTimeout(() => {
        notification.classList.add('animate-fade-out');
        setTimeout(() => {
          document.body.removeChild(notification);
          onClose(); // Close the create shipment view
        }, 500);
      }, 2000);

    } catch (error) {
      console.error('Error saving shipment:', error);
      setError(error instanceof Error ? error.message : 'Ошибка при сохранении');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="absolute inset-0 bg-white z-50 overflow-hidden flex flex-col">
      <div className="bg-white shadow px-4 py-3 flex items-center justify-between sticky top-0 z-10">
        <h1 className="text-lg font-semibold">{t.createShipment}</h1>
        <div className="flex items-center gap-2">
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto overscroll-contain">
        <div className="px-4 py-4">
        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {/* Shipment Number */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t.shipmentNumber}
          </label>
          <input
            type="text"
            value={shipmentNumber}
            pattern="[0-9]*"
            onChange={handleShipmentNumberChange}
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-lg"
            inputMode="numeric"
            placeholder="ЯР-XXXX-X"
            maxLength={11}
          />
        </div>

        {/* Tracking Numbers Count */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">{t.trackingNumbers}</span>
              {isLoading && (
                <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              )}
            </div>
            <span className="text-sm text-gray-500">{trackingNumbers.length} {t.pieces}</span>
          </div>
          {trackingNumbers.length === 0 && !isLoading && (
            <p className="text-sm text-gray-500 mt-2">No tracking numbers with 'received_china' status found</p>
          )}
        </div>

        {/* Boxes */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium">{t.boxes}</h2>
            <button
              onClick={addBox}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <Plus className="w-4 h-4" />
              {t.addBox}
            </button>
          </div>

          <div className="space-y-4">
            {boxes.map((box) => (
              <div key={box.id} className="p-4 border rounded-lg bg-gray-50">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    {t.box} №{box.id}
                  </h3>
                  {boxes.length > 1 && (
                    <button
                      onClick={() => removeBox(box.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                      <Scale className="w-4 h-4" />
                      {t.weight} ({language === 'ru' ? 'кг' : '千克'})
                    </label>
                    <input
                      type="number"
                      value={box.weight || ''}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      onChange={(e) => updateBox(box.id, 'weight', parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border rounded-lg text-center"
                      min="0"
                      placeholder="0.0"
                    />
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                      <Ruler className="w-4 h-4" />
                      {t.dimensions} ({language === 'ru' ? 'см' : '厘米'})
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        value={box.width || ''}
                        inputMode="numeric"
                        pattern="[0-9]*"
                        onChange={(e) => updateBox(box.id, 'width', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border rounded-lg text-center"
                        placeholder={language === 'ru' ? 'Ш' : t.width}
                      />
                      <input
                        type="number"
                        value={box.height || ''}
                        inputMode="numeric"
                        pattern="[0-9]*"
                        onChange={(e) => updateBox(box.id, 'height', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border rounded-lg text-center"
                        placeholder={language === 'ru' ? 'В' : t.height}
                      />
                      <input
                        type="number"
                        value={box.depth || ''}
                        inputMode="numeric"
                        pattern="[0-9]*"
                        onChange={(e) => updateBox(box.id, 'depth', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border rounded-lg text-center"
                        placeholder={language === 'ru' ? 'Г' : t.depth}
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-3 text-sm text-gray-500 font-mono">
                  {t.volume}: {calculateVolume(box).toFixed(3)} {language === 'ru' ? 'м³' : '立方米'}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Summary */}
        <div className="bg-blue-50 p-4 rounded-lg">
          <h3 className="font-medium mb-3">{t.summary}</h3>
          <div className="space-y-2 text-sm">
            <p>{t.shipmentNumberLabel}: <span className="font-mono">{shipmentNumber}</span></p>
            <p>{t.trackingNumberCount}: {
              trackingNumbers.filter(t => t.checked).reduce((total, track) => {
                const childCount = childTrackingNumbers[track.id]?.length || 0;
                return total + 1 + childCount;
              }, 0)
            } {t.pieces}</p>
            <p>{t.totalWeight}: {getTotalWeight().toFixed(2)} {language === 'ru' ? 'кг' : '千克'}</p>
            <p>{t.totalVolume}: {getTotalVolume().toFixed(3)} {language === 'ru' ? 'м³' : '立方米'}</p>
            <p>{t.density}: {(getTotalWeight() / (getTotalVolume() || 1)).toFixed(2)} {language === 'ru' ? 'кг/м³' : '千克/立方米'}</p>
          </div>
        </div>
        
        {/* Search and Quick Actions */}
        <div className="mt-4 mb-4">
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
              placeholder={t.searchTrackingNumber}
              autoComplete="off"
            />
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              <Search className="w-5 h-5" />
            </div>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
          
          {/* Quick Stats */}
          <div className="mt-2 flex items-center justify-between text-sm text-gray-500">
            <span>{t.markedCount}: {trackingNumbers.filter(t => t.checked).length} {t.of} {trackingNumbers.length}</span>
            <div className="flex items-center gap-2">
              {lastChecked && (
                <span className="font-mono">{t.lastMarked}: {lastChecked}</span>
              )}
              <button
                onClick={() => setShowScanner(true)}
                className="p-2 bg-blue-100 text-blue-600 rounded-full hover:bg-blue-200 transition-colors"
                title={t.scanTrackingNumber}
              >
                <Camera className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          {trackingNumbers
            .filter(track => 
              !searchQuery || 
              track.number.toLowerCase().includes(searchQuery.toLowerCase())
            )
            .map((track) => (
            <div 
              key={track.id}
              className={`rounded-lg border p-3 flex items-start gap-3 ${
                searchQuery && track.number.toLowerCase().includes(searchQuery.toLowerCase())
                  ? 'border-blue-500 shadow-sm'
                  : ''
              } ${
                track.order_id ? 'bg-[#E6F4EA] text-[#1E4620]' : 'bg-gray-50 text-gray-700'
              }`}
            >
              <button
                onClick={() => {
                  toggleChecked(track.id);
                  setLastChecked(track.number);
                }}
                className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                  track.checked ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300'
                }`}
              >
                {track.checked && <Check className="w-4 h-4" />}
              </button>
              <div 
                className="flex-1 min-w-0 cursor-pointer" 
                onClick={() => {
                  if (!addingChildTo) {
                    toggleChecked(track.id);
                    setLastChecked(track.number);
                  }
                }}
              > 
                <div className="font-mono text-sm">
                  {track.number}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {track.order_id ? t.receivedInChina : t.unmatchedScan}: {track.status_date.toLocaleString(
                    language === 'ru' ? 'ru-RU' : 'zh-CN',
                    { 
                      year: 'numeric',
                      month: 'numeric',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    }
                  )}
                </div>
                {track.order_id && (
                  <div className="text-xs text-gray-400 mt-1">
                    {track.order_id}
                  </div>
                )}
                {addingChildTo === track.id && (
                  <div className="mt-2">
                    <input
                      type="text"
                      value={newChildTracking}
                      onChange={(e) => setNewChildTracking(e.target.value)}
                      className="w-full text-sm p-2 border rounded-lg font-mono"
                    placeholder={t.addChildTracking}
                      autoFocus
                    />
                  </div>
                )}
                {childTrackingNumbers[track.id]?.length > 0 && (
                  <div className="mt-2 space-y-1">
                    <p className="text-xs text-gray-500">{t.childTrackings}:</p>
                    {childTrackingNumbers[track.id].map((childTracking, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 p-1 rounded">
                        <span className="font-mono flex-1">{childTracking}</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeChildTracking(track.id, childTracking);
                          }}
                          className="text-red-500 hover:text-red-600 p-1"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button
                onClick={() => addingChildTo === track.id ? 
                  handleCommentSave(track.id) :
                  setAddingChildTo(track.id)
                }
                className="p-2 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
              >
                {addingChildTo === track.id ? (
                  <Save className="w-5 h-5" />
                ) : (
                  <Plus className="w-5 h-5" />
                )}
              </button>
            </div>
          ))}
        </div>
      </div>
      </div>

      {showScanner && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex flex-col">
          <div className="bg-white p-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold">{t.scanner}</h2>
            <button
              onClick={() => setShowScanner(false)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="flex-1 relative">
            <Scanner onScan={handleScanResult} />
          </div>
        </div>
      )}


      <div className="bg-white border-t p-4 sticky bottom-0 z-10">
        <button 
          onClick={handleSendShipment}
          disabled={isSaving || !shipmentNumber.match(/^ЯР-\d{4}-\d{1,3}$/) || trackingNumbers.filter(t => t.checked).length === 0 || boxes.some(box => !box.weight || !box.width || !box.height || !box.depth)}
          className={`
            w-full py-3 rounded-lg font-medium transition-colors
            flex items-center justify-center gap-2
            bg-blue-600 hover:bg-blue-700
            disabled:opacity-50 disabled:cursor-not-allowed
            text-white
          `}
        >
          {isSaving ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              {t.saving}
            </>
          ) : (
            t.send
          )}
        </button>
      </div>
    </div>
  );
}